import React, { useState, useEffect, useRef } from 'react';
import { Grid, Row, Col, FormGroup, ControlLabel } from 'react-bootstrap';
import Select from 'react-select';
import * as API from "Admin/api/clinic";
import { saveAs } from 'file-saver';
import { labTestList, starStatusList } from '../../../Front/api/home';
import Loading from "react-loading";
import Button from "Admin/elements/CustomButton/CustomButton.jsx";
import NotificationSystem from "react-notification-system";
import { style } from "Admin/variables/Variables.jsx";
import Datetime from 'react-datetime';
import moment from 'moment';

const DownloadClinicData = () => {
    let convertToMl = 0;
    const [clinicList, setClinicList] = useState([]);
    const [formData, setFormData] = useState({ clinicId: "", startDate: "", endDate: "" });
    const [labTests, setLabTests] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const notificationSystem = useRef(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [start_date, setStart_Date] = useState("");
    const [end_date, setEnd_Date] = useState("");
    const [starStatusData, setStarStatusData] = useState([]);

    useEffect(() => {
        getClinicList();
        getLabTestList();
        getStarStatusList();
    }, []);

    const getClinicList = async () => {
        let res = await API.clinicSimpleList();
        if (res?.data?.status === "Success") {
            let selArr = res.data.data.map((obj, idx) => ({
                value: obj._id,
                label: `Dr. ${obj?.doctorName} - Clinic: (${obj.name} - ${obj.clinicId})`
            }));
            selArr.unshift({ value: "", label: "Select Clinic" });
            setClinicList([...selArr]);
        }
    };

    const getLabTestList = async () => {
        let res = await labTestList();
        if (res?.data?.status === "Success") {
            let tempList = res?.data?.testReportList?.data || [];
            tempList.forEach(obj => obj.isEmpty = false);
            setLabTests([...tempList]);
        }
    };

    const getStarStatusList = async () => {
        let res = await starStatusList();
        if (res?.data?.status === "Success") {
            let tempList = res?.data?.starStatusList?.data || [];
            tempList.forEach(obj => obj.isEmpty = false);
            setStarStatusData([...tempList]);
        }
    };

    const inputHandler = (e, field) => {
        let updatedFormData = { ...formData, [field]: e ? e.value : "" };
        setFormData(updatedFormData);
    };

    const handleStartDate = (date) => {
        let formatted = moment(date).format('YYYY-MM-DD');
        setStart_Date(formatted);
        setStartDate(moment(date).format('DD-MM-YYYY'));
    };

    const handleEndDate = (date) => {
        let formatted = moment(date).format('YYYY-MM-DD');
        setEnd_Date(formatted);
        setEndDate(moment(date).format('DD-MM-YYYY'));
    };

    const valid = current => moment().format('YYYY-MM-DD') >= moment(current).format('YYYY-MM-DD');
    const valid2 = current => {
        let today = moment().format('YYYY-MM-DD');
        let currentFormatted = moment(current).format('YYYY-MM-DD');
        return today >= currentFormatted && start_date <= currentFormatted;
    };

    const successAlert = (msg, success = true) => {
        const notification = notificationSystem.current;
        notification.addNotification({
            title: (<span data-notify="icon" className="pe-7s-check" style={{ fontSize: "30px", color: "#fff" }}></span>),
            message: <div>{msg}</div>,
            level: success ? "success" : "error",
            position: "tr",
            autoDismiss: 2
        });
    };

    const getDrugDetails = (itm) => {
        let drugDetails = `${itm.drugType}-${itm?.drugNameWithDose || ''}`;
        itm.doseTimeSlot.forEach(ele => {
            drugDetails += ` (`;
            ele.customizeFrequency.forEach(subEle => {
                if (itm.drugType === "SYP") {
                    convertToMl = 5 * eval(subEle?.key);
                }
                drugDetails += subEle?.key;
            });
            drugDetails += itm.drugType === "INJ" || itm.drugType === "INSULIN" ? " U " : itm.drugType === "SYP" ? `TSF (${convertToMl} ml) ` : "";
            drugDetails += ` at ${ele?.doseTimeSlotName})`;
            if (itm?.duration?.no && itm?.duration?.period) drugDetails += ` X ${itm.duration.no} ${itm.duration.period}`;
        });
        return drugDetails;
    };

    const downloadCSV = async () => {
        if (!formData.clinicId) return;
        setIsDownloading(true);
        try {
            let res = await API.downloadClinicData({ clinicId: formData?.clinicId, startDate: start_date, endDate: end_date });
            if (res.status === 200 && Object.keys(res.data).length > 0) {
                const csvChunks = splitCSVIntoChunks(res.data, 0.2 * 1024 * 1024);
                csvChunks.forEach((chunk, index) => {
                    const blob = new Blob([chunk], { type: "text/csv;charset=utf-8;" });
                    saveAs(blob, `ClinicData_${index + 1}.csv`);
                });
                successAlert('File Downloaded Successfully');
            } else {
                successAlert('No Data Found', false);
            }
        } catch (error) {
            successAlert('Something went wrong', false);
        } finally {
            setIsDownloading(false);
        }
    };

    const splitCSVIntoChunks = (data, maxSize) => {
        if (!data || Object.keys(data).length === 0) return [];

        let maxDrugs = 0;
        Object.values(data).forEach(entry => {
            if (entry.patientPrescriptionData) {
                maxDrugs = Math.max(maxDrugs, entry.patientPrescriptionData.length);
            }
        });

        const headers = [
            `"Patient Id"`, `"Patient Name"`, `"Gender"`, `"Age"`, `"Mobile Number"`, `"City"`, `"Using App"`, `"Visit Date"`, `"Consultation Type"`,
            ...labTests.map(test => `"${test.name}${test.unit ? ` (${test.unit})` : ""}"`),
            `"Complaints"`, `"Diagnosis"`,
            ...Array.from({ length: maxDrugs }, (_, i) => `"Drug ${i + 1}"`),
            `"Knowledge Shared"`, `"Star Status"`, `"Tags Given"`, `"Diet Given"`, `"Diet Calorie"`,
            `"Diet Guidelines Given"`, `"Message For Educator"`
        ];

        const entries = Object.values(data).filter(item => typeof item === "object" && item._id);
        const patientVisitsMap = {};

        entries.forEach(entry => {
            const patientId = entry.patientClinicId;
            if (!patientVisitsMap[patientId]) patientVisitsMap[patientId] = [];
            patientVisitsMap[patientId].push(entry);
        });

        const allRows = [];

        Object.values(patientVisitsMap).forEach(visits => {
            visits.sort((a, b) => new Date(b.visitData?.createdAt) - new Date(a.visitData?.createdAt));
            const patientDataWritten = {};

            visits.forEach((entry, index) => {
                const isFirstVisit = !patientDataWritten[entry.patientClinicId];
                if (isFirstVisit) patientDataWritten[entry.patientClinicId] = true;

                const currentVisitDate = new Date(entry.visitData?.createdAt);
                const nextVisitDate = index < visits.length - 1 ? new Date(visits[index + 1].visitData?.createdAt) : null;

                const labTestValues = labTests.map(test => {
                    const values = [];

                    (entry.labData || []).forEach(report => {
                        const reportDate = new Date(report.testReportDate);
                        if (
                            reportDate <= currentVisitDate &&
                            (!nextVisitDate || reportDate > nextVisitDate)
                        ) {
                            const match = report.patientTestReport.find(t => t._id === test._id && t.value);
                            if (match) {
                                values.push(`{Date: ${moment(reportDate).format('DD-MM-YYYY')}, Value: ${match.value}}`);
                            }
                        }
                    });

                    return values.length > 0 ? `"${values.join(" | ")}"` : `""`;
                });

                const drugEntries = Array.from({ length: maxDrugs }, (_, i) =>
                    `"${entry.patientPrescriptionData?.[i]?.drugNameWithDose ? getDrugDetails(entry.patientPrescriptionData[i]) : ""}"`
                );

                const patientStarStatus = isFirstVisit ? (starStatusData.find(ele => ele._id === entry.patientStarStatus)?.title || "") : "";
                const dietGuidelines = isFirstVisit ? (entry?.lastDiet?.[0]?.comments || []).map(c => c["1"]).join(" | ") : "";
                const educatorMsgs = (entry.EducatorMessageData || []).map(c => c?.taskName).join(" | ");
                const knowledgeShared = isFirstVisit ? (entry.patientDocumentsData || []).map(d => d.documentName).join(" | ") : "";
                const tagsGiven = isFirstVisit ? (entry.patientTags || []).map(c => c?.tagName).join(" | ") : "";
                const dietGiven = isFirstVisit ? (entry.patientDietData || []).map(d => d.treatmentName).join(" | ") : "";
                const calorie = isFirstVisit ? entry.selectedCalorie || "" : "";

                const row = [
                    `"${entry.patientClinicId || ""}"`,
                    `"${(entry.title || "") + " " + (entry.firstName || "") + " " + (entry.lastName || "")}"`,
                    `"${entry.gender || ""}"`, `"${entry.age || ""}"`, `"${entry.phone || ""}"`, `"${entry.city || ""}"`,
                    `"${entry.app ? "Yes" : "No"}"`,
                    `"${entry.visitData?.createdAt ? new Date(entry.visitData.createdAt).toLocaleDateString("en-GB") : ""}"`,
                    `"${entry?.visitData?.econsultation ? "OnLine" : "OffLine"}"`,
                    ...labTestValues,
                    `"${(entry.SymptomsData || []).map(c => c?.symptomsSubCategoryName).join(" | ")}"`,
                    `"${(entry.patientDiagnosisData || []).map(d => d?.diagonsisSubCategoryName).join(" | ")}"`,
                    ...drugEntries,
                    `"${knowledgeShared}"`,
                    `"${patientStarStatus}"`,
                    `"${tagsGiven}"`,
                    `"${dietGiven}"`,
                    `"${calorie}"`,
                    `"${dietGuidelines}"`,
                    `"${educatorMsgs}"`
                ].join(",");

                allRows.push(row);
            });
        });

        const csvChunks = [];
        let currentChunk = [headers.join(",")];
        let currentSize = new Blob([headers.join(",")]).size;

        allRows.forEach(row => {
            const rowSize = new Blob([row]).size;

            if (currentSize + rowSize > maxSize) {
                csvChunks.push(currentChunk.join("\n"));
                currentChunk = [headers.join(",")];
                currentSize = new Blob([headers.join(",")]).size;
            }

            currentChunk.push(row);
            currentSize += rowSize;
        });

        if (currentChunk.length > 1) {
            csvChunks.push(currentChunk.join("\n"));
        }

        return csvChunks;
    };




    return (
        <div className="main-content">
            {isDownloading && (
                <div className="backdrop">
                    <Loading type="spinningBubbles" color="#ffffff" height={50} width={50} />
                    <p>Downloading in progress...</p>
                </div>
            )}
            <div><NotificationSystem ref={notificationSystem} style={style} /></div>
            <Grid fluid>
                <Row>
                    <Col sm={4}>
                        <FormGroup>
                            <Col componentClass={ControlLabel}>Clinic:</Col>
                            <Select
                                name="clinicId"
                                value={formData.clinicId}
                                options={clinicList}
                                onChange={(e) => inputHandler(e, "clinicId")}
                            />
                        </FormGroup>
                    </Col>
                    <Col sm={4}>
                        <FormGroup>
                            <Col componentClass={ControlLabel}>From:</Col>
                            <Datetime
                                timeFormat={false}
                                dateFormat={"DD-MM-YYYY"}
                                value={startDate || ""}
                                onChange={handleStartDate}
                                isValidDate={valid}
                                inputProps={{ placeholder: "DD-MM-YYYY" }}
                            />
                        </FormGroup>
                    </Col>
                    <Col sm={4}>
                        <FormGroup>
                            <Col componentClass={ControlLabel}>To:</Col>
                            <Datetime
                                timeFormat={false}
                                dateFormat={"DD-MM-YYYY"}
                                value={endDate || ""}
                                onChange={handleEndDate}
                                isValidDate={valid2}
                                inputProps={{ placeholder: "DD-MM-YYYY" }}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12}>
                        <FormGroup>
                            <Button
                                className="btn btn-fill btn-primary print-button"
                                disable={!formData?.clinicId}
                                onClick={downloadCSV}
                            >
                                <span>Download CSV</span>
                            </Button>
                        </FormGroup>
                    </Col>
                </Row>
            </Grid>
            <style>{`
                .backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 18px;
                    z-index: 9999;
                }
            `}</style>
        </div>
    );
};

export default DownloadClinicData;
